/**
 * LiveScoreCard / LiveScoreAnnotation
 *
 * Chat card for free live scores. Polls /api/live-scores/games/:id every 15s
 * while the game is live, the tab is visible, and the card is mounted.
 */

'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import type { Annotation, Annotation2 } from '@/types/annotation'
import type { LiveGame, LiveGameStatus, LiveScoreTeam } from '@/lib/live-scores/types'
import { cn } from '@/lib/utils'

// --------------

type WatchPayload = {
  action?: string
  gameId?: string
  sport?: string
  snapshot?: LiveGame
  message?: string
  ok?: boolean
  error?: string
}

type SearchPayload = {
  games?: LiveGame[]
  query?: string
  unavailableSports?: Array<{ sport: string; reason: string }>
  ok?: boolean
  error?: string
}

const POLL_MS = 15_000

// --------------

function unwrapToolPayload(raw: unknown): Record<string, unknown> | null {
  let value: unknown = raw

  if (typeof value === 'string') {
    try {
      value = JSON.parse(value) as unknown
    } catch {
      return null
    }
  }

  if (!value || typeof value !== 'object' || Array.isArray(value)) return null

  const record = value as Record<string, unknown>
  let nested: unknown = record.result

  if (typeof nested === 'string') {
    try {
      nested = JSON.parse(nested) as unknown
    } catch {
      nested = undefined
    }
  }

  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return nested as Record<string, unknown>
  }

  return record
}

function readWatchPayload(
  annotation: Annotation | Annotation2
): WatchPayload | null {
  const record = annotation as Record<string, unknown>
  const raw = record['liveScores__watchGame']
  const payload = unwrapToolPayload(raw)
  return payload as WatchPayload | null
}

function readSearchPayload(
  annotation: Annotation | Annotation2
): SearchPayload | null {
  const record = annotation as Record<string, unknown>
  const raw = record['liveScores__getLiveScores']
  const payload = unwrapToolPayload(raw)
  return payload as SearchPayload | null
}

function statusLabel(status: LiveGameStatus): string {
  switch (status) {
    case 'live':
      return 'Live'
    case 'final':
      return 'Final'
    case 'scheduled':
      return 'Scheduled'
    case 'unavailable':
      return 'Unavailable'
    default:
      return status
  }
}

function formatScore(value: number | null | undefined): string {
  return typeof value === 'number' ? String(value) : '—'
}

function TeamSide({
  team,
  align
}: {
  team: LiveScoreTeam
  align: 'left' | 'right'
}) {
  const [logoFailed, setLogoFailed] = useState(false)
  const logoPair = useMemo(() => {
    const url = team.logoUrl?.trim()
    if (!url) return null
    // NHL CDN: `_dark` for light UI, `_light` for dark UI.
    if (url.includes('_light.svg')) {
      return {
        forLightUi: url.replace('_light.svg', '_dark.svg'),
        forDarkUi: url
      }
    }
    if (url.includes('_dark.svg')) {
      return {
        forLightUi: url,
        forDarkUi: url.replace('_dark.svg', '_light.svg')
      }
    }
    return { forLightUi: url, forDarkUi: url }
  }, [team.logoUrl])
  const showLogo = Boolean(logoPair) && !logoFailed

  return (
    <div
      className={cn(
        'flex min-w-0 items-center gap-2',
        align === 'right' && 'flex-row-reverse text-right'
      )}
    >
      {showLogo && logoPair ? (
        <span className="relative inline-flex size-7 shrink-0 items-center justify-center">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={logoPair.forLightUi}
            alt=""
            width={28}
            height={28}
            referrerPolicy="no-referrer"
            className="size-7 object-contain dark:hidden"
            onError={() => setLogoFailed(true)}
          />
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={logoPair.forDarkUi}
            alt=""
            width={28}
            height={28}
            referrerPolicy="no-referrer"
            className="hidden size-7 object-contain dark:block"
            onError={() => setLogoFailed(true)}
          />
        </span>
      ) : (
        <span
          className="bg-muted text-muted-foreground inline-flex size-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold uppercase"
          aria-hidden
        >
          {(team.abbreviation || team.name).slice(0, 2)}
        </span>
      )}
      <div className="min-w-0">
        <div className="text-muted-foreground text-xs uppercase tracking-wide">
          {align === 'left' ? 'Away' : 'Home'}
        </div>
        <div className="text-foreground truncate text-sm font-semibold">
          {team.abbreviation || team.name}
        </div>
      </div>
    </div>
  )
}

// --------------

function ScoreBoard({
  game,
  stale
}: {
  game: LiveGame
  stale?: boolean
}) {
  const isLive = game.status === 'live'

  return (
    <div className="border-border bg-background overflow-hidden rounded-2xl border">
      <div className="border-border/80 flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
        <div>
          <div className="text-muted-foreground text-[11px] font-medium uppercase tracking-[0.14em]">
            {game.league}
          </div>
          <div className="text-foreground text-sm font-medium">
            {game.away.name} @ {game.home.name}
          </div>
        </div>
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium',
            isLive
              ? 'border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-300'
              : game.status === 'final'
                ? 'border-emerald-500/25 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                : 'border-border bg-muted text-muted-foreground'
          )}
        >
          {isLive ? (
            <span className="relative flex size-2">
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-red-400 opacity-60" />
              <span className="relative inline-flex size-2 rounded-full bg-red-500" />
            </span>
          ) : null}
          {statusLabel(game.status)}
        </span>
      </div>

      {game.status === 'unavailable' ? (
        <div className="text-muted-foreground px-4 py-4 text-sm leading-5">
          {game.unavailableReason || 'Score not available on free feeds.'}
        </div>
      ) : (
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 px-4 py-5">
          <TeamSide team={game.away} align="left" />
          <div className="text-foreground text-center text-3xl font-semibold tabular-nums">
            {formatScore(game.away.score)}
            <span className="text-muted-foreground mx-2 text-xl font-normal">
              –
            </span>
            {formatScore(game.home.score)}
          </div>
          <TeamSide team={game.home} align="right" />
        </div>
      )}

      <div className="border-border/80 text-muted-foreground flex flex-wrap items-center justify-between gap-2 border-t px-4 py-2.5 text-[11px]">
        <span>
          {[game.period, game.clock].filter(Boolean).join(' · ') ||
            (game.startsAt
              ? new Date(game.startsAt).toLocaleString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                })
              : '—')}
          {stale ? ' · stale' : ''}
        </span>
        <span>{game.attribution}</span>
      </div>
    </div>
  )
}

// --------------

function LiveWatchCard({
  initial,
  gameId
}: {
  initial: LiveGame
  gameId: string
}) {
  const [game, setGame] = useState<LiveGame>(initial)
  const [stale, setStale] = useState(false)
  const [polling, setPolling] = useState(false)

  const refresh = useCallback(async () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      return
    }

    setPolling(true)
    try {
      const res = await fetch(
        `/api/live-scores/games/${encodeURIComponent(gameId)}`,
        { cache: 'no-store' }
      )
      if (!res.ok) return
      const data = (await res.json()) as { game?: LiveGame; stale?: boolean }
      if (data.game) {
        setGame(data.game)
        setStale(Boolean(data.stale))
      }
    } catch {
      // Keep last good snapshot
    } finally {
      setPolling(false)
    }
  }, [gameId])

  useEffect(() => {
    if (game.status !== 'live') return

    const tick = () => {
      void refresh()
    }

    const id = window.setInterval(tick, POLL_MS)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') tick()
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [game.status, refresh])

  return (
    <div className="relative max-w-md">
      <ScoreBoard game={game} stale={stale} />
      {polling && game.status === 'live' ? (
        <div className="text-muted-foreground absolute right-3 top-3">
          <Loader2 className="size-3.5 animate-spin" aria-hidden />
        </div>
      ) : null}
    </div>
  )
}

function SearchGamesCard({ search }: { search: SearchPayload }) {
  if (!search.games || search.games.length === 0) return null

  const preferred =
    search.games.find(g => g.status === 'live') || search.games[0]

  return (
    <div className="flex max-w-md flex-col gap-2">
      <LiveWatchCard initial={preferred} gameId={preferred.id} />
      {search.games.length > 1 ? (
        <p className="text-muted-foreground text-xs">
          +{search.games.length - 1} more game
          {search.games.length - 1 === 1 ? '' : 's'} today. Ask Losi to watch a
          specific team for a dedicated card.
        </p>
      ) : null}
    </div>
  )
}

// --------------

export const LiveScoreAnnotation: React.FC<{
  annotation: Annotation | Annotation2
  toolKey?: string
}> = ({ annotation, toolKey }) => {
  // Read both payloads independently. A failed watch_score must not hide a
  // successful getLiveScores card (that was the "cards vanish after gen" bug).
  const watch = readWatchPayload(annotation)
  const search = readSearchPayload(annotation)

  const preferWatchOnly = toolKey === 'liveScores__watchGame'
  const preferSearchOnly = toolKey === 'liveScores__getLiveScores'

  const showSearch =
    !preferWatchOnly &&
    Boolean(
      (search?.games && search.games.length > 0) ||
        search?.unavailableSports?.length
    )
  const showWatchCard =
    !preferSearchOnly &&
    watch?.action === 'watch_score' &&
    Boolean(watch.snapshot && watch.gameId)
  const showWatchError =
    !preferSearchOnly &&
    !showWatchCard &&
    Boolean(watch?.error || (watch && watch.ok === false)) &&
    // Don't bury a good search card under a failed watch error.
    !showSearch

  if (!showSearch && !showWatchCard && !showWatchError) {
    if (toolKey && !watch && !search) return null
    return null
  }

  return (
    <div className="flex flex-col gap-2 py-2">
      {showSearch && search?.games && search.games.length > 0 ? (
        <SearchGamesCard search={search} />
      ) : null}

      {showSearch && search?.unavailableSports?.length && !search.games?.length ? (
        <div className="border-border bg-muted/30 text-muted-foreground max-w-md rounded-2xl border p-3 text-xs leading-5">
          {search.unavailableSports.map(entry => (
            <p key={entry.sport} className="mb-1 last:mb-0">
              <span className="text-foreground font-medium uppercase">
                {entry.sport}:
              </span>{' '}
              {entry.reason}
            </p>
          ))}
        </div>
      ) : null}

      {showWatchCard && watch?.snapshot && watch.gameId ? (
        <LiveWatchCard initial={watch.snapshot} gameId={watch.gameId} />
      ) : null}

      {showWatchError ? (
        <div className="border-destructive/20 bg-destructive/10 text-destructive max-w-md rounded-2xl border p-3 text-xs leading-5">
          {watch?.error || watch?.message || 'Could not load live score.'}
        </div>
      ) : null}
    </div>
  )
}

export default LiveScoreAnnotation
